/**
 * DI-3-G — new vs present, BEFORE Start ("201 new · 13 already in the set
 * (same image)"): the folder's files against the points the data set holds
 * (`fetchAllIntakePoints`, every page), by PAYLOAD HASH only (DI-D4 c1). Exact,
 * never estimated — the counts are what the round trip will answer (the
 * present ones replay, the new ones are created).
 *
 * The rule, per file of the send plan (in-folder duplicates already dropped by
 * `sendPlan`, DI-2-2):
 *
 *   * a file WITH a payload hash is present iff a point of the set carries the
 *     same `payloadHash` (the value `images.payload_hash` stores — the parser's
 *     `payloadSha256Hex`, DI-2-1). Its file name does not matter (a `~n` wire
 *     twin, a renamed file: the same image is the same image).
 *   * a file WITHOUT one (no scan in the file — the M4T case) keys on `md5:`
 *     (as its externalId does, `sync/externalId.ts`) and counts as NEW, unless
 *     a point of the set has NO payload hash and the same file name as the
 *     file's wire name — the only trace such an image leaves on a point.
 *
 * `conflicts`: a NEW file whose wire name a point of the set already holds for
 * a different image — the server refuses the whole batch
 * (`image_filename_conflict`, DI-2-4 AC-5), so Start waits and says why.
 *
 * `newKeys` is what the add's upload keeps (the ledger never sees a present
 * file): the payload hash, or `md5:<hex>` for a hash-less file.
 */
import type { IntakePoint } from "../api/intakeTypes.js";
import { wireNames, type SyncRow } from "../sync/toDataPointInput.js";

export interface AddDiff {
  new: number;
  present: number;
  /** Row indexes of the new / present files (the index of this folder). */
  newIndexes: number[];
  presentIndexes: number[];
  /** The upload's keys: one per new file (`fileKey`). */
  newKeys: string[];
  /** Bytes of the new files — "2.6 GB to upload (new files only)". */
  newBytes: number;
  /** New files whose wire name the set holds for another image. */
  conflicts: { index: number; filename: string }[];
}

/** The key a file is known by: its payload hash, or `md5:<hex>` without one. */
export function fileKey(h: {
  payloadSha256Hex: string | null;
  md5Hex: string;
}): string {
  return h.payloadSha256Hex ?? `md5:${h.md5Hex}`;
}

export function diffAgainstSet(
  rows: readonly SyncRow[],
  points: readonly Pick<IntakePoint, "payloadHash" | "imageFilename">[],
): AddDiff {
  const hashes = new Set<string>();
  const hashlessNames = new Set<string>();
  const names = new Set<string>();
  for (const p of points) {
    if (p.payloadHash) hashes.add(p.payloadHash);
    else if (p.imageFilename) hashlessNames.add(p.imageFilename);
    if (p.imageFilename) names.add(p.imageFilename);
  }
  const wire = wireNames(rows);
  const out: AddDiff = {
    new: 0,
    present: 0,
    newIndexes: [],
    presentIndexes: [],
    newKeys: [],
    newBytes: 0,
    conflicts: [],
  };
  for (const r of rows) {
    const name = wire.get(r.index) ?? r.name;
    const h = r.hashes.payloadSha256Hex;
    const present = h ? hashes.has(h) : hashlessNames.has(name);
    if (present) {
      out.present += 1;
      out.presentIndexes.push(r.index);
      continue;
    }
    out.new += 1;
    out.newIndexes.push(r.index);
    out.newKeys.push(fileKey(r.hashes));
    out.newBytes += r.size;
    if (names.has(name)) out.conflicts.push({ index: r.index, filename: name });
  }
  return out;
}

/** "201 indexed · 13 already indexed" — the add's round trip, in the pilot's words. */
export function addSummaryLine(r: {
  created: number;
  duplicates: number;
  replayed: number;
}): string {
  return `${r.created.toLocaleString("en-US")} indexed · ${(r.duplicates + r.replayed).toLocaleString("en-US")} already indexed`;
}
